import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Container, Grid } from "@mui/material";
import PageTitle from "components/PageTitle";
import LoadingScreen from "components/Template/LoadingScreen";
import type { Territoire, Perimetre, Transfert, Commune } from "models";
import { territoiresService, perimetresService, communesService } from "services";
import { competencesUtil } from "utils";
import { useErrorHandler, withPageErrorBoundary } from "utils/errorHandling";
import TerritoirePerimetresSdeaInformations from "./components/TerritoirePerimetresSdeaInformations";
import TerritoireInformationsGenerales from "./components/TerritoireInformationsGenerales";
import InformationBox from "components/InformationBox";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Fonctions from "constants/Fonctions";
import FilteredElusList from "./components/FilteredElusList";
import { SdeaIcon } from "icons";
import PersonIcon from "@mui/icons-material/Person";
import TerritoireInformationsSdeaWithErrorBoundary from "./components/TerritoireInformationsSdea";
import type Competence from "constants/Competence";
import { useRequiredParams } from "hooks";
import { ContactsMetiersInformationBox } from "components/Informations/ContactsMetiers/ContactsMetiersInformationBox";
import { conditionsValues } from "constants/ContactsMetiers/conditionsValues";
import type { ContactMetierFilters } from "services/contactsMetiersService";
import { sortUtil } from "utils/sortUtil";

const memoizedNoTransferts: Array<Transfert> = [];

function TerritoirePage(): ReactNode {
  const { id } = useRequiredParams<{ id: string }>();
  const [territoire, setTerritoire] = useState<Territoire>();
  const [perimetres, setPerimetres] = useState<Array<Perimetre>>([]);
  const [competences, setCompetences] = useState<Array<Competence>>([]);
  const [communes, setCommunes] = useState<Array<Commune>>([]);

  const memoizedContactsMetiersFilter: Partial<ContactMetierFilters> | undefined = useMemo(
    () =>
      territoire != null
        ? {
            communesId: communes.map(({ id }) => id),
            perimetresId: perimetres.map(({ id }) => id),
            territoiresId: [territoire.id],
            competences: [...competences, conditionsValues.Empty],
          }
        : undefined,
    [communes, competences, perimetres, territoire]
  );

  const { catchErrors } = useErrorHandler();
  const { catchErrors: catchCommunesError } = useErrorHandler(true);
  const { catchErrors: catchPerimetresError } = useErrorHandler();

  useEffect(() => {
    async function getPerimetres(): Promise<void> {
      const _perimetres = await perimetresService.getByTerritoire(id);
      _perimetres.sort(
        sortUtil.combineMultipleSort(competencesUtil.sortPerimetresByCompetence, (p1, p2) =>
          p1.libelle.localeCompare(p2.libelle)
        )
      );
      setPerimetres(_perimetres);
      setCompetences(competencesUtil.getCompetencesFromPerimetres(_perimetres));
    }

    async function getTerritoire(): Promise<void> {
      const _territoire = await territoiresService.getById(id);
      document.title = _territoire.libelle;
      setTerritoire(_territoire);
    }

    async function getCommunes(): Promise<void> {
      const _communes = await communesService.getByTerritoire(id);
      setCommunes(_communes);
    }

    setPerimetres([]);
    setCompetences([]);
    setTerritoire(undefined);
    setCommunes([]);

    void catchErrors(getTerritoire);
    void catchPerimetresError(getPerimetres);
    void catchCommunesError(getCommunes);
  }, [catchCommunesError, catchErrors, catchPerimetresError, id]);

  if (territoire == null) {
    return <LoadingScreen />;
  }

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2}>
        <PageTitle
          title={territoire.libelle}
          icon={<SdeaIcon />}
          subtitle="Territoire"
          competences={competences}
        />

        <Grid container size={{ xs: 12, md: 4 }}>
          <InformationBox icon={VisibilityIcon} articleTitle="Informations Générales" hasBackground>
            <TerritoireInformationsGenerales territoireId={territoire.id} />
          </InformationBox>
        </Grid>
        <Grid container size={{ xs: 12, md: 4 }}>
          <InformationBox icon={SdeaIcon} articleTitle="Informations SDEA" hasBackground>
            <TerritoireInformationsSdeaWithErrorBoundary
              territoire={territoire}
              perimetres={perimetres}
            />
          </InformationBox>
        </Grid>
        <Grid container size={{ xs: 12, md: 4 }}>
          <InformationBox icon={PersonIcon} articleTitle="Commission permanente" hasBackground>
            <FilteredElusList
              fonctions={Fonctions.COMMISSION_PERMANENTE}
              territoireId={territoire.id}
            />
          </InformationBox>
        </Grid>

        <ContactsMetiersInformationBox
          useContactsMetiersParams={{
            $type: "territoire",
            collectiviteId: id,
            transferts: memoizedNoTransferts,
            competences,
            filter: memoizedContactsMetiersFilter,
          }}
        />

        <Grid container size={{ xs: 12, md: 6 }}>
          <InformationBox icon={PersonIcon} articleTitle="Conseil d'administration" hasBackground>
            <FilteredElusList
              fonctions={Fonctions.CONSEIL_ADMINISTRATION}
              territoireId={territoire.id}
            />
          </InformationBox>
        </Grid>
        <Grid container size={{ xs: 12, md: 6 }}>
          <InformationBox icon={PersonIcon} articleTitle="Conseil territorial" hasBackground>
            <FilteredElusList
              fonctions={[
                Fonctions.CONSEIL_TERRITORIAL,
                Fonctions.CONSEILLER_TERRITORIAL_CP_CA,
                Fonctions.CONSEILLER_TERRITORIAL_DESIGNE,
              ]}
              territoireId={territoire.id}
              sousTitresFonctions
            />
          </InformationBox>
        </Grid>
        <Grid container size={12}>
          <TerritoirePerimetresSdeaInformations perimetres={perimetres} />
        </Grid>
      </Grid>
    </Container>
  );
}

const TerritoirePageWithErrorBoundary = withPageErrorBoundary(TerritoirePage);
export default TerritoirePageWithErrorBoundary;
